import React, {ChangeEvent, FormEvent, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../hooks/useAuth';
import styles from './LoginPage.module.css';

const LoginPage = () => {
    const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        username: ''
    });
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const {login, register} = useAuth();

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setMessage('');

        try {
            if (mode === 'login') {
                await login(formData.email, formData.password);
                navigate('/');
            } else if (mode === 'register') {
                // Проверяем совпадение паролей
                if (formData.password !== formData.confirmPassword) {
                    throw new Error('Пароли не совпадают');
                }

                // Вызываем функцию регистрации
                await register(formData.email, formData.password, formData.username, formData.confirmPassword);
                setMessage('Регистрация успешна! Теперь вы можете войти.');
                setMode('login');
            } else if (mode === 'forgot') {
                // Заглушка для восстановления пароля
                // В реальном приложении здесь будет вызов API
                await new Promise(resolve => setTimeout(resolve, 1000)); // Имитация задержки
                setMessage('Инструкции по восстановлению пароля отправлены на ваш email.');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Произошла неизвестная ошибка');
        } finally {
            setIsLoading(false);
        }
    };

    const renderForm = () => {
        switch (mode) {
            case 'register':
                return (
                    <>
                        <div className={styles.formGroup}>
                            <label htmlFor="username">Имя пользователя</label>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                value={formData.username}
                                onChange={handleInputChange}
                                required
                                disabled={isLoading}
                                placeholder="Придумайте имя пользователя"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                disabled={isLoading}
                                placeholder="user@example.com"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="password">Пароль</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                disabled={isLoading}
                                placeholder="Придумайте пароль"
                                autoComplete="new-password"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="confirmPassword">Подтверждение пароля</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                required
                                disabled={isLoading}
                                placeholder="Повторите пароль"
                                autoComplete="new-password"
                            />
                        </div>
                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Регистрация...' : 'Зарегистрироваться'}
                        </button>
                    </>
                );
            case 'forgot':
                return (
                    <>
                        <div className={styles.formGroup}>
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                disabled={isLoading}
                                placeholder="user@example.com"
                            />
                        </div>
                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Отправка...' : 'Восстановить пароль'}
                        </button>
                    </>
                );
            default: // login
                return (
                    <>
                        <div className={styles.formGroup}>
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                disabled={isLoading}
                                placeholder="user@example.com"
                                autoComplete="current-password"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="password">Пароль</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                disabled={isLoading}
                                placeholder="Введите пароль"
                            />
                        </div>
                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Вход...' : 'Войти'}
                        </button>
                    </>
                );
        }
    };

    return (
        <div className={styles.loginPage}>
            <div className={styles.loginContainer}>
                <div className={styles.modeTabs}>
                    <button
                        className={`${styles.tab} ${mode === 'login' ? styles.activeTab : ''}`}
                        onClick={() => setMode('login')}
                    >
                        Вход
                    </button>
                    <button
                        className={`${styles.tab} ${mode === 'register' ? styles.activeTab : ''}`}
                        onClick={() => setMode('register')}
                    >
                        Регистрация
                    </button>
                </div>

                <h1 className={styles.title}>
                    {mode === 'login' && 'Вход в систему'}
                    {mode === 'register' && 'Регистрация'}
                    {mode === 'forgot' && 'Восстановление пароля'}
                </h1>

                {error && (
                    <div className={styles.error}>
                        {error}
                    </div>
                )}

                {message && (
                    <div className={styles.message}>
                        {message}
                    </div>
                )}

                <form onSubmit={handleSubmit} className={styles.loginForm}>
                    {renderForm()}
                </form>

                {mode === 'login' && (
                    <div className={styles.links}>
                        <button
                            type="button"
                            className={styles.linkButton}
                            onClick={() => setMode('forgot')}
                        >
                            Забыли пароль?
                        </button>
                    </div>
                )}

                {(mode === 'register' || mode === 'forgot') && (
                    <div className={styles.links}>
                        <button
                            type="button"
                            className={styles.linkButton}
                            onClick={() => setMode('login')}
                        >
                            Вернуться к входу
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoginPage;